import type { EquipmentCategory, EquipmentInfo } from '@az3fr/core';

import type { SelectOptionConfig } from '@/lib/option-generators';

/**
 * Category labels for equipment groups
 */
const CATEGORY_LABELS: Record<EquipmentCategory, string> = {
	calisthenics: 'Bodyweight',
	free_weights: 'Free Weights',
	gym_machine: 'Machines',
	functional: 'Functional',
	accessory: 'Accessories',
	cardio: 'Cardio',
	mobility: 'Mobility',
	other: 'Other',
};

/**
 * Defines the display order of equipment categories.
 * More accessible/common categories first.
 */
const CATEGORY_ORDER: EquipmentCategory[] = [
	'calisthenics', // Bodyweight first (most accessible)
	'free_weights', // Common gym equipment
	'gym_machine', // Machines
	'functional', // Bands, etc.
	'accessory', // Add-ons
	'cardio', // Cardio equipment
	'mobility', // Recovery
	'other', // Misc
];

/**
 * Groups equipment by category and creates select options with group headers.
 *
 * Group headers are implemented as disabled items with isGroupHeader flag.
 * This approach:
 * - Works seamlessly with virtualized scrolling
 * - Maintains simple component API (no special optgroup logic)
 * - Provides visual grouping without accessibility complexity
 *
 * @param equipments - Array of equipment items to group
 * @returns Flat array of options with injected group headers
 *
 * @example
 * const options = createGroupedEquipmentOptions(equipments);
 * // Result:
 * // [
 * //   { value: '__group_calisthenics', label: 'Bodyweight', disabled: true, isGroupHeader: true },
 * //   { value: 'eq_1', label: 'Bodyweight' },
 * //   { value: '__group_free_weights', label: 'Free Weights', disabled: true, isGroupHeader: true },
 * //   { value: 'eq_2', label: 'Barbell' },
 * //   { value: 'eq_3', label: 'Dumbbell' },
 * //   ...
 * // ]
 */
export function createGroupedEquipmentOptions(
	equipments: EquipmentInfo[],
): SelectOptionConfig<string>[] {
	// Group equipments by category
	const grouped = equipments.reduce(
		(acc, equipment) => {
			const category = equipment.category;
			if (!acc[category]) {
				acc[category] = [];
			}
			acc[category].push(equipment);
			return acc;
		},
		{} as Record<EquipmentCategory, EquipmentInfo[]>,
	);

	// Build flat options array with group headers
	const result: SelectOptionConfig<string>[] = [];

	for (const category of CATEGORY_ORDER) {
		const items = grouped[category];
		if (!items?.length) {
			continue;
		}

		// Add group header as disabled item
		result.push({
			value: `__group_${category}`,
			label: CATEGORY_LABELS[category],
			disabled: true,
			isGroupHeader: true,
		});

		// Add equipment items sorted by displayOrder
		const sortedItems = items
			.slice()
			.sort((a: EquipmentInfo, b: EquipmentInfo) => a.displayOrder - b.displayOrder);

		for (const item of sortedItems) {
			result.push({
				value: item.id,
				label: item.name,
			});
		}
	}

	return result;
}
